/**
 * 러너의 유효 엔진(effective engines) 판정 SSOT.
 *
 * 서버(`api/src/services/daemonEngines.ts`)와 같은 술어를 쓴다:
 * 수동 지정(`supportedEngines`) 우선 → 없으면 신선한 자동 탐지(`detectedEngines`) → 둘 다 없으면 제한 없음.
 * 한쪽만 고치면 UI가 서버는 반드시 409로 거절할 엔진을 후보로 노출하게 되므로 항상 함께 수정한다.
 */

/// 자동 탐지 스냅샷을 후보 판정에 쓰는 신선도 창(서버 DETECTED_ENGINES_FRESHNESS_MS와 같은 값).
export const DETECTED_ENGINES_FRESHNESS_MS = 60 * 60 * 1000;

export type DaemonEngineSources = {
  supportedEngines: readonly string[];
  detectedEngines?: readonly string[] | null;
  detectedEnginesAt?: string | null;
};

export const isDetectedEnginesFresh = (daemon: DaemonEngineSources, now: number = Date.now()): boolean => {
  if (!daemon.detectedEnginesAt) {
    return false;
  }

  const detectedAt = new Date(daemon.detectedEnginesAt).getTime();
  return Number.isFinite(detectedAt) && now - detectedAt < DETECTED_ENGINES_FRESHNESS_MS;
};

/// 빈 배열이면 "제한 없음"을 의미한다(서버와 동일한 규약).
export const resolveEffectiveEngines = (daemon: DaemonEngineSources, now: number = Date.now()): string[] => {
  if (daemon.supportedEngines.length > 0) {
    return [...daemon.supportedEngines];
  }

  return isDetectedEnginesFresh(daemon, now) ? [...(daemon.detectedEngines ?? [])] : [];
};

/// 플랜 상세 실행 정책 셀렉트 후보. 유효 엔진이 비면 제한 없음(전체 엔진). 공유 단위 allowedEngines가 있으면 한 번 더 좁힌다.
export const resolvePlanPolicyEngineOptions = (
  daemon: (DaemonEngineSources & { allowedEngines?: readonly string[] }) | null | undefined,
  allEngines: readonly string[],
  now: number = Date.now(),
): string[] => {
  if (!daemon) {
    return [...allEngines];
  }

  const effectiveEngines = resolveEffectiveEngines(daemon, now);
  const supported =
    effectiveEngines.length === 0 ? [...allEngines] : allEngines.filter((engine) => effectiveEngines.includes(engine));
  if (!daemon.allowedEngines || daemon.allowedEngines.length === 0) {
    return supported;
  }

  return supported.filter((engine) => daemon.allowedEngines!.includes(engine));
};
